/**
 * Horizontal tab bar and its matching panel wrapper.
 *
 * Rendered as the last child of a PageHeader, so the active tab's underline
 * lands exactly on the header's bottom rule (`-mb-px` pulls the row down by
 * the width of that rule).
 *
 * `idPrefix` scopes the ARIA wiring, so two tab sets can coexist on one page.
 * `testIdPrefix` defaults to it and exists only to preserve pre-existing
 * data-testid values on pages whose selectors predate this component.
 *
 * Usage:
 *   <Tabs tabs={TABS} active={tab} onChange={setTab} label="Site sections" idPrefix="site" />
 *   <TabPanel id="issues" active={tab} idPrefix="site"> ... </TabPanel>
 */
export const tabId = (prefix, id) => `${prefix}-tab-${id}`;
export const panelId = (prefix, id) => `${prefix}-panel-${id}`;

export const Tabs = ({ tabs, active, onChange, label, idPrefix, testIdPrefix }) => (
  <div role="tablist" aria-label={label} className="flex -mb-px overflow-x-auto">
    {tabs.map(({ id, label: tabLabel, badge }) => {
      const isActive = id === active;
      return (
        <button
          key={id}
          type="button"
          role="tab"
          id={tabId(idPrefix, id)}
          aria-selected={isActive}
          aria-controls={panelId(idPrefix, id)}
          data-testid={`${testIdPrefix || idPrefix}-tab-${id}`}
          onClick={() => onChange(id)}
          className={`whitespace-nowrap px-4 py-2.5 text-sm border-b-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F] focus-visible:ring-inset ${
            isActive
              ? "border-[#16324F] text-[#16324F] font-medium"
              : "border-transparent text-ink-muted hover:text-ink"
          }`}
        >
          {tabLabel}
          {badge != null && <span className="ml-2 font-mono text-xs text-ink-muted">{badge}</span>}
        </button>
      );
    })}
  </div>
);

export const TabPanel = ({ id, active, idPrefix, children, ...rest }) =>
  active !== id ? null : (
    <div role="tabpanel" id={panelId(idPrefix, id)} aria-labelledby={tabId(idPrefix, id)} {...rest}>
      {children}
    </div>
  );
